import {
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IStyledOption } from '../framework/constants/documents.constants';
import { CustomOverlayComponent } from '../framework/custom-overlay/custom-overlay.component';
import { IDefaultOverlayListItem } from '../framework/custom-overlay/mention-overlay/mention-overlay.component';

type CustomOverlayPosition =
  | 'screen-center'
  | { x: number; y: number }
  | { top: number; left: 'center' }
  | { left: number; top: 'center' };

type CustomOverlayConfig = {
  hasBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  backdropClass?: string;
  keyboardSelectionIsActive?: boolean; // todo: make keyboard selection active on all overlays
};

export enum CUSTOM_OVERLAY_VIEWS {
  DEFAULT,
  STYLED_OPTIONS_LIST,
  CROPPER,
}

/**
 * Service to handle opening and closing of custom overlays with global position strategy
 * If you need to open an overlay that is connected to a specific element, use the @component OverlayService instead
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CustomOverlayService {
  overlayRef: OverlayRef;
  componentRef: ComponentRef<any>;
  // not the greatest typing of the world
  private _data$ = new BehaviorSubject<
    File | { listItems: IStyledOption[] } | IDefaultOverlayListItem[]
  >(null);
  private _selectedView$ = new BehaviorSubject<CUSTOM_OVERLAY_VIEWS>(null);
  private _outputData$ = new Subject();
  private _isOpened$ = new BehaviorSubject(false);

  private _keyboardSelectionIsActive = false;
  private _keyboardSelection = 0;

  constructor(private overlay: Overlay) {}

  get data$() {
    return this._data$.asObservable();
  }

  get selectedView$() {
    return this._selectedView$.asObservable();
  }

  get outputData$() {
    return this._outputData$.asObservable().pipe(filter((data) => !!data));
  }

  get keyboardSelection() {
    return this._keyboardSelection;
  }

  get keyboardSelectionIsActive() {
    return this._keyboardSelectionIsActive;
  }

  openOverlay(
    position: CustomOverlayPosition = 'screen-center',
    data: File | { listItems: IStyledOption[] } | IDefaultOverlayListItem[],
    selectedView: CUSTOM_OVERLAY_VIEWS,
    customConfig: CustomOverlayConfig = {
      hasBackdrop: true,
      closeOnBackdropClick: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      keyboardSelectionIsActive: false,
    },
  ) {
    if (this.overlayRef?.hasAttached()) {
      console.info('An overlay is already open, updating data instead.');
      this.setInputData(data);
      return;
    }

    this._keyboardSelectionIsActive = customConfig.keyboardSelectionIsActive;

    this._data$.next(data);

    this._isOpened$.next(true);

    this._selectedView$.next(selectedView);

    const config: OverlayConfig = {};

    let positionStrategy: GlobalPositionStrategy | FlexibleConnectedPositionStrategy;

    if (position === 'screen-center') {
      positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
    } else if ('top' in position && position.left === 'center') {
      positionStrategy = this.overlay
        .position()
        .global()
        .centerHorizontally()
        .top(`${position.top}px`);
    } else if ('left' in position && position.top === 'center') {
      positionStrategy = this.overlay
        .position()
        .global()
        .centerVertically()
        .left(`${position.left}px`);
    } else {
      const positions = [
        new ConnectionPositionPair(
          { originX: 'end', originY: 'bottom' },
          { overlayX: 'start', overlayY: 'top' },
        ),
        new ConnectionPositionPair(
          { originX: 'end', originY: 'top' },
          { overlayX: 'start', overlayY: 'bottom' },
        ),
        new ConnectionPositionPair(
          { originX: 'start', originY: 'top' },
          { overlayX: 'end', overlayY: 'bottom' },
        ),
        new ConnectionPositionPair(
          { originX: 'start', originY: 'bottom' },
          { overlayX: 'end', overlayY: 'top' },
        ),
      ];

      positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo({ x: position.x ?? 0, y: position.y ?? 0 })
        .withPositions(positions);
    }

    config.positionStrategy = positionStrategy;
    config.hasBackdrop = customConfig?.hasBackdrop ?? true;
    config.backdropClass = customConfig.backdropClass;
    this.overlayRef = this.overlay.create(config);
    const componentPortal = new ComponentPortal(CustomOverlayComponent);
    this.componentRef = this.overlayRef.attach(componentPortal);

    this.overlayRef.backdropClick().subscribe((_) => {
      if (!customConfig.closeOnBackdropClick) {
        console.log('closeOnBackdropClick prevented');
        return;
      }

      this.overlayRef.dispose();
      this._isOpened$.next(false);
      this._outputData$.next({});
    });

    // close overlay on right click
    this.overlayRef.backdropElement.oncontextmenu = (e) => {
      e.preventDefault();
      this.overlayRef.dispose();
      this._isOpened$.next(false);
      this._outputData$.next({});
    };
  }

  closeOverlay() {
    if (!this.overlayRef) console.info('No overlay was detected as being opened.');

    this.overlayRef?.dispose();
    this._isOpened$.next(false);
  }

  emitOutputData(data) {
    this._outputData$.next(data);
  }

  setInputData(data) {
    this._keyboardSelection = 0;
    this._data$.next(data);
  }

  setKeyboardSelection(index: number) {
    this._keyboardSelection = index;
  }

  /**
   * This method should be called when the keyboard selection is confirmed
   */
  emitKeyboardSelection() {
    // todo: generalise across all overlay types, this currently only works for default overlay
    if (this._selectedView$.value !== CUSTOM_OVERLAY_VIEWS.DEFAULT) {
      console.error('This method is only supported for default overlay');
      return;
    }

    this.emitOutputData(this._data$.value[this._keyboardSelection]);
    this.closeOverlay();
  }

  isOpened() {
    return this.overlayRef?.hasAttached();
  }

  isOpened$() {
    return this._isOpened$.asObservable();
  }
}
